import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn } from 'lucide-react';
import { useAdminAuth } from './AuthContext';

export default function AdminLogin() {
  const { status, login } = useAdminAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Sudco Admin — Sign in';
  }, []);

  if (status === 'authed') {
    const dest = (location.state && location.state.from) || '/admin';
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grid-bg">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm glass rounded-2xl p-8 shadow-glow"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg bg-gradient-brand flex items-center justify-center font-display font-bold text-white">
            S
          </span>
          <div>
            <div className="font-display font-bold text-lg">Sudco Admin</div>
            <div className="text-xs text-text-muted">Sign in to continue</div>
          </div>
        </div>

        <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Email</label>
        <input
          type="email"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-4 bg-surface border border-border rounded-lg px-3 py-2 text-text focus:outline-none focus:border-accent"
        />

        <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-4 bg-surface border border-border rounded-lg px-3 py-2 text-text focus:outline-none focus:border-accent"
        />

        {error && (
          <div className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-gradient-brand text-white font-medium px-4 py-2 rounded-lg hover:shadow-glow transition-shadow disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <LogIn size={16} />
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </motion.form>
    </div>
  );
}
